<template>
  <div>
    <model-table
      :collection="movie.movieTerms"
      submodel
      orderable
      display-order-title="Set Priority"
      display-order-description="Movie terms will be applied to the movie in the order they appear, from top to bottom.
      Showings that match the topmost term will be reported against the topmost term.
      Showings that do not match any higher terms will be reported against the default term.
      Showings must include all specified showing badges on the term to be matched.
      You can rearrange the order to change which term is prioritized first."
      :order-options="['numberOfShowingBadges', 'numberOfPriorityShowingBadges', 'totalShowingBadgeDisplayOrder']"
      order-field="priorityOrder"
      :columns="[
        { field: 'id' },
        { name: 'priority', field: 'priorityOrder', component: 'chip' },
        { field: 'format' },
        { name: 'flatFee', field: row => row.hasFlatFee ? row.flatFee : null, component: 'money' },
        { name: 'previewTerms', field: row => row.hasFlatFee ? null : $_.map(row.previewMovieTermWeeks, movieTermWeek => `${movieTermWeek.termPercent}%`), component: 'chips' },
        { name: 'releaseTerms', field: row => row.hasFlatFee ? null : $_.map(row.releaseMovieTermWeeks, movieTermWeek => `${movieTermWeek.termPercent}%`), component: 'chips' },
        { name: 'showings', field: 'showingCount', component: 'chip' },
        { name: 'valid', field: 'validityIcon', component: 'icon' },
        { name: 'library', component: 'library-link', hide: currentCircuit.isLibrary, align: 'right' },
        { name: 'actions', align: 'right' }
      ]"
      :actions="[
        {
          label: 'Mark as Settled',
          icon: 'done_all',
          disabled: (_) => false,
          handler: (movieTerm) => movieTerm.action = (movieTerm.action === 'settle' ? null : 'settle')
        },
        {
          label: 'Mark as Unsettled',
          icon: 'remove_done',
          disabled: (_) => false,
          handler: (movieTerm) => movieTerm.action = (movieTerm.action === 'unsettle' ? null : 'unsettle')
        }
      ]"
      emit-only-add
      @add="movieTerm => $refs.modelForm.show(movieTerm, { movie: movie })"
    >
      <movie-term-form
        ref="modelForm"
        :original-movie-terms-data="movie.originalData.movieTerms"
        @change="handleMovieTermChange"
      />
      <template #set-order-button-label>
        Set Priority
      </template>
    </model-table>
  </div>
</template>

<script>
import Movie from 'src/models/movie/Movie'
import ModelTable from 'src/modules/admin/components/ModelTable'
import MovieTermForm from 'src/modules/admin/movie/MovieTermForm'

export default {
  name: 'MovieTermTable',
  components: {
    ModelTable,
    MovieTermForm
  },
  configuration: ['currentCircuit'],
  props: {
    movie: {
      type: Movie,
      required: true
    }
  },
  data () {
    return {
      originalMovieTermData: {}
    }
  },
  methods: {
    didShowingBadgeIdsChange (movieTerm) {
      return !_.isEmpty(
        _.xor(
          this.originalMovieTermData[movieTerm.id]?.showingBadgeIds,
          movieTerm.showingBadgeIds
        )
      )
    },
    handleMovieTermChange (movieTerm) {
      this.prioritizeMovieTerm(movieTerm)
    },
    prioritizeMovieTerm (movieTerm) {
      this.originalMovieTermData[movieTerm.id] ||= {
        showingBadgeIds: movieTerm.originalData.showingBadgeIds,
        index: null
      }

      if (movieTerm.isRemote && !this.didShowingBadgeIdsChange(movieTerm)) {
        if (this.originalMovieTermData[movieTerm.id]?.index !== null) {
          const dropResult = {
            removedIndex: _.findIndex(this.movie.movieTerms.data, { id: movieTerm.id }),
            addedIndex: this.originalMovieTermData[movieTerm.id].index,
            payload: movieTerm
          }
          this.originalMovieTermData[movieTerm.id].index = null
          const reorderedList = this.$mh.applyDrag(this.movie.movieTerms.data, dropResult)
          this.handleReorder({ reorderedList })
        }

        return
      }

      let newIndex = this.movie.movieTerms.data.length - 2
      const movieTermIndex = _.findIndex(this.movie.movieTerms.data, { id: movieTerm.id })

      _.each(_.orderBy(this.movie.movieTerms.data, 'priorityOrder'), (term, index) => {
        if (term.id === movieTerm.id) return

        if (movieTerm.numberOfShowingBadges < term.numberOfShowingBadges) return

        if (movieTerm.numberOfShowingBadges === term.numberOfShowingBadges &&
            movieTerm.numberOfPriorityShowingBadges < term.numberOfPriorityShowingBadges) return

        if (movieTerm.numberOfShowingBadges === term.numberOfShowingBadges &&
            movieTerm.numberOfPriorityShowingBadges === term.numberOfPriorityShowingBadges &&
            movieTerm.totalShowingBadgeDisplayOrder > term.totalShowingBadgeDisplayOrder) return

        newIndex = Math.min(index, this.movie.movieTerms.data.length - 2)

        // We have found our index, break the loop
        return false
      })

      if (movieTermIndex !== (newIndex - 1)) {
        this.originalMovieTermData[movieTerm.id].index ||= movieTermIndex

        const dropResult = {
          removedIndex: movieTermIndex,
          addedIndex: newIndex,
          payload: movieTerm
        }

        const reorderedList = this.$mh.applyDrag(this.movie.movieTerms.data, dropResult)
        this.handleReorder({ reorderedList })
      }
    },
    handleReorder ({ reorderedList }) {
      this.movie.movieTerms.data = reorderedList
      this.movie.movieTerms.validateDisplayOrder('priorityOrder')
    }
  }
}
</script>
